import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { PairHeadsetPage } from "features/device";
import { OrderHistoryPage } from "features/orders";
import Profile from "features/account/pages/ProfilePage";
import { UploadPage as UploadNewTitle } from "features/creator-upload";
import RoleGuard from "shared/components/routing/RoleGuard";
import NotFound from "./NotFound";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { Link } from "react-router-dom";
import {
  BrowseAllPage,
  CategoryPage,
  SingleTitleView,
  UserHome,
} from "features/catalog";
import { CreatorLibrary } from "features/creator-library";
import Sidebar from "./Sidebar";
import { UserWatchlist, WatchlistProvider } from "features/watchlist";
import {
  CREATOR_LAYOUT_ROLES,
  HOME_SHELL_ROLES,
  USER_LAYOUT_ROLES,
  isUserLayoutRole,
  mainContentClassForRole,
  normalizeUserRole,
} from "shared/auth/constants/roles";
import { RoleMatch } from "shared/components/routing/RoleMatch";
import { CuratorMovieReview } from "features/curator-review";
import { AnalyticsPlaceholder } from "features/analytics";

function HomeShell() {
  const role = normalizeUserRole(getCookie("userRole"));
   const isCreator = role === "CREATOR" || role === "ADMIN";
  const { pathname } = useLocation();
  const isTopLevelBrowseRoute =
    pathname === "/browse" || pathname === "/home/browse";
  const isTopLevelWatchlistRoute = pathname === "/watchlist";
  const isHomeLandingRoute = pathname === "/home" || pathname === "/home/home";

  const mainContentClass = mainContentClassForRole(role);

  return (
    <div
      className={
        isHomeLandingRoute ? "layout layout--home-landing" : "layout"
      }
    >
      <Routes>
        <Route
          path="upload"
          element={
            <RoleGuard allowedRoles={CREATOR_LAYOUT_ROLES}>
              <UploadNewTitle />
            </RoleGuard>
          }
        />
        <Route
          path="*"
          element={
            <RoleGuard
              allowedRoles={HOME_SHELL_ROLES}
              unauthorizedFallback={
                <div className="library-creator-content">
                  <div className="main-library">
                    <div className="top-container-library">
                      <div className="left-top-container-library">
                        <div className="title-top-container-library">
                          Access denied
                        </div>
                        <div className="subtitle-top-container-library">
                          You don’t have permission to view this page.
                        </div>
                        <div style={{ marginTop: "24px" }}>
                          <Link
                            to="/login"
                            className="library-top-container-button"
                          >
                            Go to Login
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            >
              <WatchlistProvider enabled={isUserLayoutRole(role)}>
                {role ? (
                  <>
                    <Sidebar />
                    <div
                      className={`main-content ${mainContentClass}`.trim()}
                    >
                      <Routes>
                        <Route
                          index
                          element={
                            isCreator ? (
                              <Navigate to="/home/library" replace />
                            ) : role === "CURATOR" ? (
                              isTopLevelBrowseRoute ? (
                                <BrowseAllPage />
                              ) : (
                                <Navigate to="/home/movie-review" replace />
                              )
                            ) : (
                              <RoleMatch
                                allowedRoles={CREATOR_LAYOUT_ROLES}
                                fallback={
                                  <RoleMatch
                                    allowedRoles={USER_LAYOUT_ROLES}
                                    fallback={<NotFound />}
                                  >
                                    {isTopLevelBrowseRoute ? (
                                      <BrowseAllPage />
                                    ) : isTopLevelWatchlistRoute ? (
                                      <UserWatchlist />
                                    ) : (
                                      <UserHome />
                                    )}
                                  </RoleMatch>
                                }
                              >
                                {isTopLevelBrowseRoute ? (
                                  <BrowseAllPage />
                                ) : (
                                  <UserHome />
                                )}
                              </RoleMatch>
                            )
                          }
                        />
                        <Route
                          path="movie-review"
                          element={
                            <RoleMatch allowedRoles={["CURATOR", "ADMIN"]} fallback={<NotFound />}>
                              <CuratorMovieReview />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="library"
                          element={
                            <RoleMatch
                              allowedRoles={["CREATOR", "ADMIN"]}
                              fallback={
                                <RoleMatch
                                  allowedRoles={["CURATOR"]}
                                  fallback={
                                    <RoleMatch
                                      allowedRoles={USER_LAYOUT_ROLES}
                                      fallback={<NotFound />}
                                    >
                                      <Navigate to="/home/watchlist" replace />
                                    </RoleMatch>
                                  }
                                >
                                  <Navigate to="/home/movie-review" replace />
                                </RoleMatch>
                              }
                            >
                              <CreatorLibrary />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="browse"
                          element={
                            <RoleMatch
                              allowedRoles={HOME_SHELL_ROLES}
                              fallback={<NotFound />}
                            >
                              <BrowseAllPage />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="category/:categoryId"
                          element={
                            <RoleMatch
                              allowedRoles={HOME_SHELL_ROLES}
                              fallback={<NotFound />}
                            >
                              <CategoryPage />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="title/:titleId"
                          element={
                            <RoleMatch
                              allowedRoles={HOME_SHELL_ROLES}
                              fallback={<NotFound />}
                            >
                              <SingleTitleView />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="home"
                          element={
                            <RoleMatch
                              allowedRoles={USER_LAYOUT_ROLES}
                              fallback={<NotFound />}
                            >
                              <UserHome />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="watchlist"
                          element={
                            <RoleMatch
                              allowedRoles={HOME_SHELL_ROLES}
                              fallback={<NotFound />}
                            >
                              <UserWatchlist />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="pair_headset"
                          element={
                            <RoleMatch
                              allowedRoles={HOME_SHELL_ROLES}
                              fallback={<NotFound />}
                            >
                              <PairHeadsetPage />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="order_history"
                          element={
                            <RoleMatch
                              allowedRoles={[
                                "CURATOR",
                                ...USER_LAYOUT_ROLES,
                              ]}
                              fallback={<NotFound />}
                            >
                              <OrderHistoryPage />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="profile"
                          element={
                            <RoleMatch
                              allowedRoles={[
                                ...CREATOR_LAYOUT_ROLES,
                                ...USER_LAYOUT_ROLES,
                              ]}
                              fallback={<NotFound />}
                            >
                              <Profile />
                            </RoleMatch>
                          }
                        />
                        <Route
                          path="analytics"
                          element={
                            <RoleMatch
                              allowedRoles={CREATOR_LAYOUT_ROLES}
                              fallback={<NotFound />}
                            >
                              <AnalyticsPlaceholder />
                            </RoleMatch>
                          }
                        />
                        <Route path="unknown" element={<NotFound />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </div>
                  </>
                ) : (
                  <NotFound />
                )}
              </WatchlistProvider>
            </RoleGuard>
          }
        />
      </Routes>
    </div>
  );
}

export default HomeShell;
